import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@tanstack/react-query';
import { ChevronLeft, ChevronRight, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { toolsApi } from '@/api/tools';
import { unwrapError } from '@/api/client';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import type { ToolRecord } from '@/api/types';
import { Step1Kind } from './Step1Kind';
import { Step2Metadata } from './Step2Metadata';
import { Step3Inputs } from './Step3Inputs';
import { Step4Target } from './Step4Target';
import { Step5Test } from './Step5Test';
import {
  draftFromRecord,
  draftToRecord,
  emptyDraft,
  validateFields,
  validateMetadata,
  validateTarget,
  type WizardDraft,
} from './state';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  editing: ToolRecord | null;
  onSaved: () => void;
}

export function ToolWizard({ open, onOpenChange, editing, onSaved }: Props): JSX.Element {
  const { t } = useTranslation();
  const stepLabels = [
    t('tools.wizard.stepLabels.kind'),
    t('tools.wizard.stepLabels.meta'),
    t('tools.wizard.stepLabels.inputs'),
    t('tools.wizard.stepLabels.target'),
    t('tools.wizard.stepLabels.test'),
  ];

  const [step, setStep] = React.useState(0);
  const [draft, setDraft] = React.useState<WizardDraft>(() =>
    editing ? draftFromRecord(editing) : emptyDraft(),
  );
  const [savedName, setSavedName] = React.useState<string | null>(editing?.toolName ?? null);

  React.useEffect(() => {
    if (open) {
      setStep(0);
      setDraft(editing ? draftFromRecord(editing) : emptyDraft());
      setSavedName(editing?.toolName ?? null);
    }
  }, [open, editing]);

  const patch = (p: Partial<WizardDraft>): void => setDraft((d) => ({ ...d, ...p }));

  const saveMutation = useMutation({
    mutationFn: async (record: ToolRecord) => {
      if (editing) {
        return toolsApi.update(record.toolName, record);
      }
      return toolsApi.create(record);
    },
    onSuccess: (res) => {
      toast.success(editing ? t('tools.wizard.updatedToast') : t('tools.wizard.createdToast'));
      setSavedName(res.tool.toolName);
      onSaved();
      setStep(4);
    },
    onError: (err) => toast.error(unwrapError(err)),
  });

  const canGoNext = (): boolean => {
    switch (step) {
      case 0:
        return true;
      case 1:
        return validateMetadata(draft).ok;
      case 2:
        return validateFields(draft).ok;
      case 3:
        return validateTarget(draft).ok;
      default:
        return true;
    }
  };

  const onSave = (): void => {
    if (!validateMetadata(draft).ok || !validateFields(draft).ok || !validateTarget(draft).ok) {
      toast.error(t('tools.wizard.draftErrors'));
      return;
    }
    let record: ToolRecord;
    try {
      record = draftToRecord(draft);
    } catch (err) {
      toast.error(t('tools.wizard.invalidDraft', { message: err instanceof Error ? err.message : String(err) }));
      return;
    }
    saveMutation.mutate(record);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>
            {editing ? t('tools.wizard.editTool', { name: editing.toolName }) : t('tools.wizard.newTool')}
          </DialogTitle>
          <DialogDescription>{t('tools.wizard.subtitle')}</DialogDescription>
        </DialogHeader>

        <Stepper current={step} labels={stepLabels} />

        <div className="max-h-[60vh] overflow-y-auto py-2">
          {step === 0 ? (
            <Step1Kind
              value={draft.kind}
              onChange={(k) => patch({ ...emptyDraft(k), toolName: draft.toolName, description: draft.description })}
              locked={editing !== null}
            />
          ) : null}
          {step === 1 ? (
            <Step2Metadata draft={draft} onChange={patch} nameLocked={editing !== null} />
          ) : null}
          {step === 2 ? <Step3Inputs draft={draft} onChange={patch} /> : null}
          {step === 3 ? <Step4Target draft={draft} onChange={patch} /> : null}
          {step === 4 ? (
            <Step5Test draft={draft} onChange={patch} canRun={savedName !== null} />
          ) : null}
        </div>

        <div className="flex items-center justify-between border-t pt-3">
          <Button
            variant="ghost"
            onClick={() => setStep((s) => Math.max(0, s - 1))}
            disabled={step === 0 || saveMutation.isPending}
            className="gap-1"
          >
            <ChevronLeft className="h-3 w-3" />
            {t('common.previous')}
          </Button>
          <div className="text-xs text-muted-foreground">
            {t('tools.wizard.step', { n: step + 1, total: stepLabels.length })}
          </div>
          {step < 3 ? (
            <Button
              onClick={() => setStep((s) => Math.min(stepLabels.length - 1, s + 1))}
              disabled={!canGoNext()}
              className="gap-1"
            >
              {t('common.next')}
              <ChevronRight className="h-3 w-3" />
            </Button>
          ) : step === 3 ? (
            <Button onClick={onSave} disabled={saveMutation.isPending || !canGoNext()} className="gap-1.5">
              {saveMutation.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : null}
              {editing ? t('common.save') : t('common.create')}
            </Button>
          ) : (
            <Button onClick={() => onOpenChange(false)} variant="default">
              {t('common.close')}
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Stepper({ current, labels }: { current: number; labels: string[] }): JSX.Element {
  return (
    <ol className="flex items-center gap-1 text-[11px] uppercase">
      {labels.map((label, i) => {
        const active = i === current;
        const done = i < current;
        return (
          <li
            key={label}
            className={
              'flex flex-1 items-center justify-center gap-1.5 rounded border px-2 py-1 ' +
              (active
                ? 'border-primary bg-primary/10 font-semibold text-primary'
                : done
                  ? 'border-muted bg-muted/40 text-muted-foreground'
                  : 'border-muted text-muted-foreground')
            }
          >
            <span className="font-mono">{i + 1}</span>
            <span>{label}</span>
          </li>
        );
      })}
    </ol>
  );
}
